import { createRng, type Rng } from '../../../core/rng';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, vignette, type Painter } from '../../art/paint';
import { lerpColor } from '../../color';
import { hsl, type World } from '../../world';

const TAU = Math.PI * 2;
const FAR = hsl(235, 42, 16);

interface Tones {
  readonly bg: number;
  readonly ink: number;
  readonly rim: number;
  readonly ceil: number;
  readonly L: number;
  readonly A1: number;
  readonly A2: number;
}

type Gable = 'curve' | 'step' | 'flat';

interface House {
  readonly x: number;
  readonly w: number;
  readonly top: number;
  readonly base: number;
  readonly gable: Gable;
  readonly wall: number;
  readonly accent: number;
  readonly focal: boolean;
}

interface Rail {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

function gableHeight(g: Gable, s: number): number {
  if (g === 'curve') return s * 1.1;
  if (g === 'step') return s * 1.02;
  return s * 0.2;
}

function wallOf(t: Tones, rng: Rng): { wall: number; accent: number } {
  const pick = rng.int(0, 3);
  if (pick === 0) return { wall: lerpColor(FAR, t.A1, 0.1), accent: t.A1 };
  if (pick === 1) return { wall: lerpColor(FAR, t.A2, 0.12), accent: t.A2 };
  return { wall: lerpColor(FAR, t.L, 0.14), accent: t.L };
}

function row(
  rng: Rng,
  t: Tones,
  s: number,
  from: number,
  to: number,
  base: number,
  topAt: (xc: number, g: Gable) => number,
  focalX: number,
): House[] {
  const out: House[] = [];
  const gables: readonly Gable[] = ['curve', 'step', 'flat'];
  let x = from;
  let last: Gable = 'flat';
  while (x < to) {
    const w = s * rng.range(3.2, 4.4);
    const focal = focalX >= x && focalX < x + w;
    let gable: Gable = focal ? 'curve' : rng.pick(gables);
    if (!focal && gable === last) gable = gables[(gables.indexOf(gable) + 1) % gables.length] ?? 'flat';
    last = gable;
    const tone = focal ? { wall: lerpColor(FAR, t.A2, 0.14), accent: t.A2 } : wallOf(t, rng);
    out.push({ x, w, top: topAt(x + w / 2, gable), base, gable, wall: tone.wall, accent: tone.accent, focal });
    x += w;
  }
  return out;
}

function gablePath(ctx: CanvasRenderingContext2D, hs: House, s: number): void {
  const { x, w, top, gable } = hs;
  const gh = gableHeight(gable, s);
  ctx.beginPath();
  if (gable === 'curve') {
    ctx.moveTo(x, top);
    ctx.lineTo(x, top - 0.2 * gh);
    ctx.quadraticCurveTo(x + 0.3 * w, top - 0.2 * gh, x + 0.32 * w, top - 0.62 * gh);
    ctx.quadraticCurveTo(x + 0.34 * w, top - gh, x + 0.5 * w, top - gh);
    ctx.quadraticCurveTo(x + 0.66 * w, top - gh, x + 0.68 * w, top - 0.62 * gh);
    ctx.quadraticCurveTo(x + 0.7 * w, top - 0.2 * gh, x + w, top - 0.2 * gh);
    ctx.lineTo(x + w, top);
  } else if (gable === 'step') {
    const a = s * 0.22;
    const b = s * 0.5;
    ctx.moveTo(x, top);
    ctx.lineTo(x, top - a);
    ctx.lineTo(x + 0.22 * w, top - a);
    ctx.lineTo(x + 0.22 * w, top - a - b);
    ctx.lineTo(x + 0.38 * w, top - a - b);
    ctx.lineTo(x + 0.38 * w, top - gh);
    ctx.lineTo(x + 0.62 * w, top - gh);
    ctx.lineTo(x + 0.62 * w, top - a - b);
    ctx.lineTo(x + 0.78 * w, top - a - b);
    ctx.lineTo(x + 0.78 * w, top - a);
    ctx.lineTo(x + w, top - a);
    ctx.lineTo(x + w, top);
  } else {
    ctx.rect(x - s * 0.1, top - gh, w + s * 0.2, gh);
  }
  ctx.closePath();
}

function archPath(ctx: CanvasRenderingContext2D, x: number, y: number, ww: number, wh: number): void {
  const r = ww / 2;
  ctx.moveTo(x - r, y + wh);
  ctx.lineTo(x - r, y + r);
  ctx.arc(x, y + r, r, Math.PI, 0);
  ctx.lineTo(x + r, y + wh);
  ctx.closePath();
}

function house(p: Painter, t: Tones, hs: House, s: number, rng: Rng, rails: Rail[]): void {
  const { ctx } = p;
  const { x, w, top, base } = hs;
  const cornice = lerpColor(hs.wall, t.ceil, 0.5);
  ctx.fillStyle = rgba(hs.wall);
  ctx.fillRect(x, top, w, base - top);
  gablePath(ctx, hs, s);
  ctx.fill();
  ctx.strokeStyle = rgba(cornice, 0.9);
  ctx.lineWidth = Math.max(1, s * 0.07);
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.fillStyle = rgba(cornice);
  ctx.fillRect(x - s * 0.1, top - s * 0.05, w + s * 0.2, s * 0.1);
  ctx.fillRect(x, base - s * 0.14, w, s * 0.14);
  ctx.fillStyle = rgba(lerpColor(hs.wall, t.bg, 0.35));
  ctx.fillRect(x + w - s * 0.08, top, s * 0.08, base - top);
  const lit = lerpColor(FAR, t.A1, 0.45);
  const dark = lerpColor(t.bg, t.ink, 0.4);
  const shutter = lerpColor(hs.wall, hs.accent, 0.22);
  if (hs.gable === 'curve') {
    const oy = top - gableHeight('curve', s) * 0.52;
    ctx.fillStyle = hs.focal ? rgba(lit, 0.55) : rgba(dark);
    ctx.beginPath();
    ctx.arc(x + w / 2, oy, s * 0.17, 0, TAU);
    ctx.fill();
    ctx.fillStyle = rgba(cornice);
    ctx.beginPath();
    ctx.arc(x + w / 2, top - gableHeight('curve', s) - s * 0.1, s * 0.1, 0, TAU);
    ctx.fill();
  }
  const ww = s * 0.42;
  const wh = s * 0.78;
  const cols = Math.max(1, Math.floor((w - s * 0.5) / (s * 0.95)));
  const gap = w / cols;
  for (let y = top + s * 0.55; y + wh < base - s * 1.75; y += s * 1.55) {
    for (let c = 0; c < cols; c++) {
      const wx = x + gap * (c + 0.5);
      const on = rng.chance(hs.focal ? 0.8 : 0.42);
      if (calmAt(p, wx, y + wh / 2, 0.85)) continue;
      ctx.fillStyle = rgba(shutter, 0.9);
      ctx.fillRect(wx - ww * 0.5 - s * 0.16, y + ww * 0.3, s * 0.13, wh - ww * 0.3);
      ctx.fillRect(wx + ww * 0.5 + s * 0.03, y + ww * 0.3, s * 0.13, wh - ww * 0.3);
      ctx.beginPath();
      archPath(ctx, wx, y, ww, wh);
      ctx.fillStyle = on ? rgba(lit, 0.55) : rgba(dark);
      ctx.fill();
      const rw = hs.focal ? gap * 0.5 : ww * 0.85;
      rails.push({ x: wx, y: y + wh, w: rw });
    }
  }
  const dx = x + w / 2;
  if (!calmAt(p, dx, base - s * 0.6, 0.85)) {
    ctx.beginPath();
    archPath(ctx, dx, base - s * 1.3, s * 0.62, s * 1.16);
    ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.5));
    ctx.fill();
  }
}

function balcony(p: Painter, t: Tones, r: Rail, s: number): void {
  const { ctx } = p;
  if (calmAt(p, r.x, r.y, 0.85)) return;
  const half = r.w;
  const railH = s * 0.32;
  ctx.fillStyle = rgba(lerpColor(FAR, t.ceil, 0.7));
  ctx.fillRect(r.x - half - s * 0.06, r.y, half * 2 + s * 0.12, s * 0.07);
  ctx.strokeStyle = rgba(t.rim, 0.22);
  ctx.lineWidth = Math.max(0.8, s * 0.03);
  ctx.beginPath();
  ctx.moveTo(r.x - half, r.y - railH);
  ctx.lineTo(r.x + half, r.y - railH);
  const bars = Math.max(3, Math.round((half * 2) / (s * 0.12)));
  for (let i = 0; i <= bars; i++) {
    const bx = r.x - half + (i / bars) * half * 2;
    ctx.moveTo(bx, r.y - railH);
    ctx.lineTo(bx, r.y);
  }
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(r.x - half + s * 0.08, r.y + s * 0.15, s * 0.08, -Math.PI / 2, Math.PI);
  ctx.moveTo(r.x + half - s * 0.08 + s * 0.08, r.y + s * 0.15);
  ctx.arc(r.x + half - s * 0.08, r.y + s * 0.15, s * 0.08, 0, Math.PI * 1.5);
  ctx.stroke();
}

function serpentina(ctx: CanvasRenderingContext2D, x: number, y: number, len: number, amp: number, color: number, width: number): void {
  const loops = Math.max(2, Math.round(len / (amp * 2.2)));
  const steps = loops * 12;
  ctx.strokeStyle = rgba(color, 0.5);
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const k = i / steps;
    const th = k * loops * TAU;
    const px = x + Math.sin(th) * amp * (0.6 + 0.4 * k);
    const py = y + k * len - Math.cos(th) * amp * 0.35;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();
}

function garland(p: Painter, t: Tones, s: number, x0: number, y0: number, x1: number, y1: number, sag: number, rng: Rng): void {
  const { ctx } = p;
  const span = Math.hypot(x1 - x0, y1 - y0);
  const n = Math.max(6, Math.round(span / (s * 0.55)));
  ctx.strokeStyle = rgba(lerpColor(FAR, t.rim, 0.35), 0.4);
  ctx.lineWidth = Math.max(1, s * 0.035);
  ctx.beginPath();
  for (let i = 0; i <= 40; i++) {
    const q = catenary(x0, y0, x1, y1, sag, i / 40);
    if (i === 0) ctx.moveTo(q.x, q.y);
    else ctx.lineTo(q.x, q.y);
  }
  ctx.stroke();
  const tones = [t.A1, t.A2, t.L];
  for (let i = 1; i < n; i++) {
    const q = catenary(x0, y0, x1, y1, sag, i / n);
    const len = s * rng.range(1.1, 2.1);
    const sway = s * rng.range(-0.25, 0.25);
    const tone = tones[i % tones.length] ?? t.A1;
    if (calmAt(p, q.x, q.y + len * 0.6, 0.95)) continue;
    const half = s * 0.11;
    ctx.fillStyle = rgba(lerpColor(FAR, tone, 0.6), 0.55);
    ctx.beginPath();
    ctx.moveTo(q.x - half, q.y);
    ctx.quadraticCurveTo(q.x - half + sway, q.y + len * 0.5, q.x - half * 0.4 + sway * 0.6, q.y + len);
    ctx.lineTo(q.x + half * 0.4 + sway * 0.6, q.y + len * 0.92);
    ctx.quadraticCurveTo(q.x + half + sway, q.y + len * 0.5, q.x + half, q.y);
    ctx.closePath();
    ctx.fill();
  }
}

function cobbles(p: Painter, t: Tones, s: number, y0: number, rng: Rng): void {
  const { ctx, w, h } = p;
  ctx.fillStyle = rgba(lerpColor(t.bg, t.ink, 0.3));
  ctx.fillRect(0, y0, w, h - y0);
  ctx.fillStyle = rgba(lerpColor(FAR, t.ceil, 0.35), 0.8);
  ctx.fillRect(0, y0, w, Math.max(1, s * 0.06));
  const fills = [0.35, 0.58, 0.8].map((k) => lerpColor(t.bg, FAR, k));
  const paths = fills.map(() => new Path2D());
  let y = y0 + s * 0.1;
  while (y < h) {
    const depth = (y - y0) / Math.max(1, h - y0);
    const rh = s * (0.34 + 0.75 * depth);
    let x = -s * rng.range(0, 1);
    while (x < w + s) {
      const cw = rh * rng.range(1.25, 2.1);
      const gap = rh * 0.13;
      const path = paths[rng.int(0, paths.length)];
      if (path !== undefined) {
        const mx = x + cw / 2;
        const my = y + rh / 2;
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * TAU + rng.range(-0.25, 0.25);
          const rx = (cw / 2 - gap) * rng.range(0.82, 1.05);
          const ry = (rh / 2 - gap) * rng.range(0.82, 1.05);
          const px = mx + Math.cos(a) * rx;
          const py = my + Math.sin(a) * ry;
          if (i === 0) path.moveTo(px, py);
          else path.lineTo(px, py);
        }
        path.closePath();
      }
      x += cw;
    }
    y += rh;
  }
  paths.forEach((path, i) => {
    ctx.fillStyle = rgba(fills[i] ?? FAR);
    ctx.fill(path);
  });
}

type Kind = 'hat' | 'bun';

function boneco(p: Painter, t: Tones, x: number, hy: number, r: number, ground: number, kind: Kind): void {
  const { ctx } = p;
  const sil = lerpColor(t.bg, t.ink, 0.35);
  const face = lerpColor(FAR, t.L, 0.15);
  const edge = rgba(t.L, 0.28);
  const thin = Math.max(1, r * 0.04);
  const sy = hy + r * 1.2;
  const accent = kind === 'hat' ? t.L : t.A2;
  const gown = lerpColor(sil, accent, 0.13);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(x - r * 0.9, sy);
  ctx.quadraticCurveTo(x - r * 1.05, sy + (ground - sy) * 0.5, x - r * 1.65, ground);
  ctx.quadraticCurveTo(x, ground + r * 0.18, x + r * 1.65, ground);
  ctx.quadraticCurveTo(x + r * 1.05, sy + (ground - sy) * 0.5, x + r * 0.9, sy);
  ctx.closePath();
  ctx.fillStyle = rgba(gown);
  ctx.fill();
  ctx.strokeStyle = edge;
  ctx.lineWidth = thin;
  ctx.stroke();
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = rgba(t.rim, 0.07);
  ctx.lineWidth = thin;
  ctx.beginPath();
  for (const f of [-0.55, 0, 0.55]) {
    ctx.moveTo(x + f * r * 0.6, sy + r * 0.4);
    ctx.lineTo(x + f * r * 1.5, ground);
  }
  ctx.stroke();
  ctx.strokeStyle = rgba(lerpColor(FAR, t.A2, 0.4), 0.7);
  ctx.lineWidth = r * 0.24;
  ctx.beginPath();
  ctx.moveTo(x - r * 1.0, sy + r * 0.2);
  ctx.lineTo(x + r * 1.3, sy + r * 2.3);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(FAR, t.A1, 0.4), 0.7);
  for (let i = 0; i < 3; i++) {
    ctx.beginPath();
    ctx.arc(x - r * 0.15, sy + r * (0.75 + i * 0.5), r * 0.08, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(lerpColor(FAR, t.L, 0.25), 0.55);
  for (let i = -3; i <= 3; i++) {
    ctx.beginPath();
    ctx.arc(x + i * r * 0.48, ground - r * 0.05, r * 0.12, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
  const sleeve = lerpColor(sil, accent, 0.22);
  const ruff = lerpColor(FAR, t.A1, 0.32);
  for (const side of [-1, 1]) {
    const hx = x + side * r * 1.45;
    const hyy = sy + r * 1.95;
    ctx.beginPath();
    ctx.moveTo(x + side * r * 0.85, sy + r * 0.15);
    ctx.quadraticCurveTo(x + side * r * 1.65, sy + r * 0.8, hx, hyy);
    ctx.strokeStyle = edge;
    ctx.lineWidth = r * 0.4 + thin * 2;
    ctx.stroke();
    ctx.strokeStyle = rgba(sleeve);
    ctx.lineWidth = r * 0.4;
    ctx.stroke();
    ctx.fillStyle = rgba(ruff, 0.85);
    ctx.beginPath();
    ctx.ellipse(hx, hyy, r * 0.24, r * 0.1, 0, 0, TAU);
    ctx.fill();
    ctx.fillStyle = rgba(face);
    ctx.beginPath();
    ctx.arc(hx, hyy + r * 0.2, r * 0.17, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(ruff, 0.9);
  ctx.beginPath();
  ctx.ellipse(x, sy, r * 1.05, r * 0.3, 0, 0, TAU);
  ctx.fill();
  for (let i = 0; i < 9; i++) {
    const a = (i / 8) * Math.PI;
    ctx.beginPath();
    ctx.arc(x + Math.cos(a) * r * 1.0, sy + Math.sin(a) * r * 0.28, r * 0.17, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(face);
  ctx.beginPath();
  ctx.arc(x, hy, r, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.L, 0.3);
  ctx.lineWidth = thin;
  ctx.stroke();
  const hair = lerpColor(t.bg, t.ink, 0.5);
  ctx.fillStyle = rgba(hair);
  ctx.beginPath();
  ctx.arc(x, hy, r * 1.02, Math.PI * 1.08, Math.PI * 1.92);
  ctx.quadraticCurveTo(x, hy - r * 0.45, x - r * 0.93, hy - r * 0.4);
  ctx.fill();
  if (kind === 'hat') {
    ctx.fillStyle = rgba(sil);
    ctx.beginPath();
    ctx.ellipse(x, hy - r * 0.8, r * 1.0, r * 0.17, 0, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x - r * 0.62, hy - r * 0.8);
    ctx.lineTo(x - r * 0.55, hy - r * 1.85);
    ctx.lineTo(x + r * 0.55, hy - r * 1.85);
    ctx.lineTo(x + r * 0.62, hy - r * 0.8);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.lineWidth = thin;
    ctx.stroke();
    ctx.fillStyle = rgba(lerpColor(FAR, t.A2, 0.45), 0.75);
    ctx.fillRect(x - r * 0.6, hy - r * 1.12, r * 1.2, r * 0.2);
  } else {
    ctx.fillStyle = rgba(hair);
    ctx.beginPath();
    ctx.arc(x + r * 0.1, hy - r * 1.05, r * 0.42, 0, TAU);
    ctx.fill();
    ctx.strokeStyle = edge;
    ctx.lineWidth = thin;
    ctx.stroke();
    const fx = x + r * 0.5;
    const fy = hy - r * 0.95;
    ctx.fillStyle = rgba(lerpColor(FAR, t.A2, 0.55), 0.8);
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * TAU;
      ctx.beginPath();
      ctx.arc(fx + Math.cos(a) * r * 0.14, fy + Math.sin(a) * r * 0.14, r * 0.11, 0, TAU);
      ctx.fill();
    }
    ctx.fillStyle = rgba(lerpColor(FAR, t.A1, 0.6), 0.85);
    ctx.beginPath();
    ctx.arc(fx, fy, r * 0.08, 0, TAU);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x - r * 0.98, hy + r * 0.3, r * 0.07, 0, TAU);
    ctx.arc(x + r * 0.98, hy + r * 0.3, r * 0.07, 0, TAU);
    ctx.fill();
  }
  const eye = lerpColor(face, t.rim, 0.55);
  for (const side of [-1, 1]) {
    ctx.fillStyle = rgba(eye, 0.85);
    ctx.beginPath();
    ctx.arc(x + side * r * 0.33, hy - r * 0.02, r * 0.11, 0, TAU);
    ctx.fill();
    ctx.fillStyle = rgba(t.ink);
    ctx.beginPath();
    ctx.arc(x + side * r * 0.33, hy, r * 0.055, 0, TAU);
    ctx.fill();
    ctx.fillStyle = rgba(t.A2, 0.3);
    ctx.beginPath();
    ctx.arc(x + side * r * 0.52, hy + r * 0.32, r * 0.16, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = rgba(t.ink, 0.7);
  ctx.lineWidth = Math.max(1, r * 0.06);
  ctx.beginPath();
  ctx.arc(x, hy + r * 0.12, r * 0.36, Math.PI * 0.22, Math.PI * 0.78);
  ctx.stroke();
}

function confetti(p: Painter, t: Tones, s: number, street: number): void {
  const { ctx, w, h } = p;
  const rng = createRng('bloco-confetti');
  const tones = [t.A1, t.A2, t.L];
  const n = Math.round(Math.min(130, Math.max(50, (90 * w * h) / (1280 * 800))));
  for (let i = 0; i < n; i++) {
    const onStreet = i % 3 !== 0;
    const x = rng.float() * w;
    const y = onStreet ? street + rng.float() * (h - street) : h * 0.2 + rng.float() * (street - h * 0.2);
    const size = s * rng.range(0.05, 0.1);
    const tone = tones[i % tones.length] ?? t.A1;
    const rot = rng.float() * TAU;
    if (calmAt(p, x, y, 0.95)) continue;
    ctx.fillStyle = rgba(lerpColor(FAR, tone, 0.7), onStreet ? 0.4 : 0.5);
    ctx.beginPath();
    if (i % 2 === 0) ctx.arc(x, y, size, 0, TAU);
    else {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.rect(-size, -size * 0.6, size * 2, size * 1.2);
      ctx.restore();
    }
    ctx.fill();
  }
}

function crowd(p: Painter, t: Tones, s: number): void {
  const { ctx, w, h } = p;
  const rng = createRng('bloco-crowd');
  const body = lerpColor(t.bg, t.ink, 0.55);
  const tones = [t.A1, t.A2, t.L];
  const step = s * 0.95;
  let i = 0;
  for (let x = -s * 0.4; x < w + s; x += step * rng.range(0.75, 1.15)) {
    const hr = s * rng.range(0.28, 0.36);
    const hy = h - s * rng.range(0.85, 1.2);
    if (calmAt(p, x, hy, 0.85)) continue;
    const raised = rng.chance(0.35);
    const flag = raised && rng.chance(0.4);
    ctx.fillStyle = rgba(body);
    ctx.strokeStyle = rgba(t.rim, 0.14);
    ctx.lineWidth = Math.max(1, s * 0.03);
    ctx.beginPath();
    ctx.ellipse(x, hy + hr * 2.4, hr * 1.7, hr * 1.5, 0, Math.PI, TAU);
    ctx.lineTo(x + hr * 1.7, h + 2);
    ctx.lineTo(x - hr * 1.7, h + 2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(x, hy, hr, 0, TAU);
    ctx.fill();
    ctx.stroke();
    if (raised) {
      const side = i % 2 === 0 ? 1 : -1;
      const ax = x + side * hr * 1.2;
      const tx = ax + side * hr * 0.6;
      const ty = hy - hr * 2.6;
      ctx.strokeStyle = rgba(body);
      ctx.lineWidth = hr * 0.55;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(ax, hy + hr * 1.3);
      ctx.lineTo(tx, ty);
      ctx.stroke();
      if (flag) {
        ctx.fillStyle = rgba(lerpColor(FAR, tones[i % tones.length] ?? t.A1, 0.6), 0.55);
        ctx.beginPath();
        ctx.moveTo(tx, ty - hr * 0.2);
        ctx.lineTo(tx + side * hr * 1.6, ty + hr * 0.3);
        ctx.lineTo(tx, ty + hr * 0.8);
        ctx.closePath();
        ctx.fill();
      }
    }
    i++;
  }
}

export function paint(p: Painter, look: World): void {
  const { ctx, w, h, cx, unit } = p;
  const pal = look.palette;
  const t: Tones = { bg: pal.bg, ink: pal.ink, rim: pal.rim, ceil: look.ceil, L: pal.incoming.circle, A1: pal.held, A2: pal.spot };
  const aspect = w / h;
  const portrait = aspect < 0.8;
  const k = !portrait && aspect < 1.25 ? 0.8 : 1;
  const s = unit * 0.11 * k;
  const layout = createRng('bloco-layout');
  const street = portrait ? h * 0.74 : h * 0.88;

  const sky = ctx.createLinearGradient(0, 0, 0, street);
  sky.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.5)));
  sky.addColorStop(0.45, rgba(t.bg));
  sky.addColorStop(1, rgba(lerpColor(t.bg, FAR, 0.6)));
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, h);

  const starTop = portrait ? h * 0.24 : h * 0.55;
  for (let i = 0; i < 46; i++) {
    const x = layout.float() * w;
    const y = layout.float() * starTop;
    const r = layout.range(0.5, 1.3) * Math.max(1, unit / 380);
    const a = layout.range(0.18, 0.45);
    if (calmAt(p, x, y, 0.9)) continue;
    ctx.fillStyle = rgba(t.rim, a);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.fill();
  }
  glowDot(ctx, cx, portrait ? h * 0.22 : h * 0.6, (portrait ? 0.7 : 0.45) * w, t.A2, 0.07);

  const rails: Rail[] = [];
  const houses: House[] = [];
  if (portrait) {
    const focalX = w * 0.22;
    const topAt = (xc: number, g: Gable): number => {
      const focal = Math.abs(xc - focalX) < s * 2.3;
      const raw = focal ? h * 0.215 + gableHeight(g, s) : h * layout.range(0.22, 0.26);
      return Math.max(raw, h * 0.2 + gableHeight(g, s));
    };
    houses.push(...row(layout, t, s, -s * 0.3, w + s * 0.3, street, topAt, focalX));
  } else {
    const far = lerpColor(t.bg, FAR, 0.7);
    ctx.fillStyle = rgba(far);
    let fx = w * 0.3;
    while (fx < w * 0.7) {
      const fw = s * layout.range(2.4, 3.4) * 0.6;
      const ft = h * 0.6 + s * layout.range(-0.3, 0.3);
      ctx.fillRect(fx, ft, fw + 1, street - ft);
      ctx.fillRect(fx + fw * 0.3, ft - s * 0.3, fw * 0.4, s * 0.3);
      fx += fw;
    }
    const inner = w * 0.34;
    const left = (xc: number, g: Gable): number => {
      const f = Math.min(1, Math.max(0, xc / inner));
      return Math.max(h * (0.1 + 0.2 * f + layout.range(-0.015, 0.015)), gableHeight(g, s) + s * 0.2);
    };
    const right = (xc: number, g: Gable): number => left(w - xc, g);
    houses.push(...row(layout, t, s, -s * 0.3, inner, street, left, -1));
    houses.push(...row(layout, t, s, w - inner, w + s * 0.3, street, right, -1));
  }
  for (const hs of houses) house(p, t, hs, s, layout, rails);
  for (const r of rails) balcony(p, t, r, s);

  if (p.level >= 2) {
    const serp = createRng('bloco-serp');
    const tones = [t.A1, t.A2, t.L];
    rails.forEach((r, i) => {
      const go = serp.chance(0.65);
      const len = s * serp.range(0.7, 1.5);
      const off = serp.range(-0.6, 0.6) * r.w;
      if (!go || calmAt(p, r.x + off, r.y + len * 0.5, 0.85)) return;
      serpentina(ctx, r.x + off, r.y, len, s * 0.1, lerpColor(FAR, tones[i % tones.length] ?? t.A1, 0.7), Math.max(1, s * 0.035));
    });
  }

  cobbles(p, t, s, street, layout);

  if (portrait) garland(p, t, s, -w * 0.02, h * 0.205, w * 1.02, h * 0.2, h * 0.05, layout);
  else garland(p, t, s, w * 0.06, h * 0.13, w * 0.94, h * 0.11, h * 0.12, layout);

  if (portrait) {
    const r = Math.max(s, w * 0.06);
    boneco(p, t, w * 0.8, h * 0.8, r, h - s * 0.3, 'hat');
  } else {
    const r = Math.min(s * 1.6, Math.max((cx - 1.08 * unit) / 4.2, s * 1.1)) * k;
    const bx = Math.max(2.2 * r, Math.min(0.13 * w, cx - 1.25 * unit));
    const ground = h * 0.95;
    boneco(p, t, bx, h * 0.38, r, ground, 'hat');
    boneco(p, t, w - bx, h * 0.38, r, ground, 'bun');
  }

  if (p.level >= 3) confetti(p, t, s, street);
  if (p.level >= 4) crowd(p, t, s);

  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.05, true);
  vignette(p, t.ink, 0.45);
}
